
import { CategoryReference, TagCategory } from 'apprise-core/category'
import { Language } from 'apprise-core/language'
import { Tag, TagReference } from 'apprise-core/tag'
import { utils } from 'apprise-core/utils'

export type TagCache = ReturnType<ReturnType<ReturnType<typeof makeTagCache>['withCategories']>['withTags']>
export type TagCategoryCache = ReturnType<TagCache['of']>


export type TagNameResolver = (name: Record<string, string> | undefined) => string | undefined

export const makeTagCache = (langOrResolver?: Language | TagNameResolver) => {

    const lang = typeof langOrResolver === 'function' ? undefined : langOrResolver ?? 'en'
    const resolver = typeof langOrResolver === 'function' ? langOrResolver : undefined

    const withCategories = (categories: TagCategory[]) => {

        const withTags = (tags: Tag[]) => {

            const codecache: Record<CategoryReference, Record<string, Tag>> = {}
            const namecache: Record<CategoryReference, Record<string, Tag>> = {}
            const idcache: Record<CategoryReference, Record<TagReference, Tag>> = {}
            const catcache: Record<CategoryReference, TagCategory> = {}

            const tagsOfCategory = (category: CategoryReference) =>
                tags.filter(t => t.category === category)

            const lookupCategory = (category: CategoryReference) =>
                categories.find(c => c.id === category)

            const l: TagNameResolver = resolver ?? ((name: Record<string, string> | undefined) => name?.[lang!])

            const catself = (category: CategoryReference) => ({

                all: (onlyactive = false) =>  tagsOfCategory(category).filter(tag => onlyactive ? tag.lifecycle.state === 'active' : true),

                id2tag: (tag: TagReference | undefined) => {
                    if (tag === undefined) return tag
                    if (!idcache[category])
                        idcache[category] = utils().index(tagsOfCategory(category)).by(t => t.id)
                    return idcache[category][tag]
                },

                code2tag: (code: string | undefined) => {
                    if (code === undefined) return undefined
                    if (!codecache[category])
                        codecache[category] = utils().index(tagsOfCategory(category)).by(t => t.code?.toLowerCase?.() ?? t.code)
                    return codecache[category][code.toLowerCase?.() ?? code]
                },

                code2id: (code: string | undefined, fallback: string = code!) =>
                    self.code2tag(code).of(category)?.id ?? fallback,

                name2tag: (name: string | undefined) => {
                    if (name === undefined) return undefined
                    if (!namecache[category])
                        namecache[category] = utils().index(tagsOfCategory(category)).by(t => t.name.en?.toLowerCase?.() ?? t.name.en)
                    return namecache[category][name.toLocaleLowerCase?.() ?? name]
                },

                name2id: (name: string | undefined, fallback: string = name!) =>
                    self.name2tag(name).of(category)?.name.en ?? fallback,

                id2code: (id: TagReference | undefined, fallback: string = id!) =>
                    self.id2tag(id).of(category)?.code ?? fallback,

                id2name: (id: TagReference | undefined, fallback: TagReference = id!) =>
                    l(self.id2tag(id).of(category)?.name) ?? fallback
            })

            const self = {

                lookupCategory,

                
                of: catself,

                id2tag: (tag: TagReference | undefined) => ({
                    of: (category: CategoryReference) => self.of(category).id2tag(tag)
                }),

                code2tag: (code: string | undefined) => ({
                    of: (category: CategoryReference) => self.of(category).code2tag(code)
                }),

                code2id: (code: any | undefined, fallback: string = code ? `${code}` : code) => ({
                    of: (category: CategoryReference) => self.of(category).code2id(code, fallback)
                }),

                name2tag: (name: string | undefined) => ({
                    of: (category: CategoryReference) => self.of(category).name2tag(name)
                }),

                name2id: (name: any | undefined, fallback: string = name ? `${name}` : name) => ({
                    of: (category: CategoryReference) => self.of(category).name2id(name, fallback)
                }),

                id2code: (id: TagReference | undefined, fallback: string = id!) => ({
                    of: (category: CategoryReference) => self.of(category).id2code(id, fallback)
                }),

                id2name: (id: TagReference | undefined, fallback: TagReference = id!) => ({
                    of: (category: CategoryReference) => self.of(category).id2name(id, fallback)
                }),

                id2catname: (category: CategoryReference, fallback: CategoryReference = category) => {
                    const cat = self.id2cat(category)
                    return cat ? l(cat.name) : fallback
                },

                id2cat: (category: CategoryReference) => {
                    if (!catcache[category])
                        catcache[category] = lookupCategory(category)!
                    return catcache[category]
                }
            }

            return self
        }

        return { withTags }
    }

    return { withCategories }
}
